import {
  getContestReportById,
  getRecommandById,
  getAIAnalysisById,
  getContestReportStatisticsById,
  getReportRecordsById,
  getReportHistoryById
} from '@/apis/report'
import { createReportRequestScope } from '@/utils/report-request-scope'
import { createRequestGeneration } from '@/utils/request-generation'
import { reportClientError } from '@/utils/report-client-error'
import { ERROR_CODE, failureOutcome, successOutcome } from '@/report/errors'
import {
  HISTORY_STATUS,
  historyFailureState,
  historyIsFinal,
  historySuccessState,
  initialHistoryState,
  loadingHistoryState
} from '@/report/history-state'

// 請求範圍（報告改版工單 14；站5 修正 H1、第 8 項）：報告頁殼（pages/Report/index.vue）先發主報告與 /discussion，
// 再載入渲染器 chunk；渲染器掛載時照舊呼叫同一批 action。規則見 utils/report-request-scope.js：
// - 進行中：同一 type＋token＋lang 共用同一個 promise，不重打。
// - 本次頁殼載入內已結束（成功或失敗）：渲染器的自動流程不重打（主報告失敗或回 200 但沒有 record 時，舊渲染器掛載
//   不會再打一次；沒有 AI 分析的 /discussion 同理）。強制重抓（fetchReport／fetchRecommand 帶 force: true）不受限。
// - 頁殼每次 load() 先 dispatch beginShellLoad 清空紀錄（下次打開報告、SPA 內換報告都會重試）；token／lang 變動同樣清空。
// 快取短路（reportData 有值、recommandData.riskLevel 有值就不打）只擋自動流程，跨頁殼載入仍有效；強制重抓不吃快取
// （工單 17：舊渲染器「重新整理」鈕與頁殼錯誤畫面的重試因此真的重抓，主報告並帶 noWait=1）。
// /history（報告改版工單 25 PR-B）走同一個請求範圍與報告世代；快取短路＝結果已確定（src/report/history-state.js 的 historyIsFinal）。
const requestScope = createReportRequestScope()

// 報告世代（工單 17 站5）：token／lang 一換（initReport 的 CLEAR_DATA）就換世代；換之前發出、之後才回來的
// 主報告與 /discussion 回應整個丟掉（不 commit、不記 settled），不會蓋掉新報告的資料與結果。沿用 utils/request-generation.js。
const tokenGenerations = createRequestGeneration()
let currentGeneration = tokenGenerations.next()

// 錯誤細節（報告改版工單 17）：每支 fetch 結束後記一份「結果」（outcome；純資料、不含報告內容），給錯誤畫面分類、
// 回報碼與上報用（src/report/errors.js 的 successOutcome／failureOutcome）。既有的 error.<type>（布林）照舊。
const emptyOutcome = () => ({
  report: null,
  statistics: null,
  records: null,
  aiAnalysis: null,
  recommand: null,
  history: null
})
const outcomeOfError = error => (error && error.reportOutcome) || failureOutcome({})
// 解密失敗（前端自生 CLIENT_DECRYPT）一律上報，不論主請求或次要請求（工單 17 站5；同報告只報一次，去重在 reportClientError）
const reportDecryptFailure = (token, outcome) => {
  if (outcome && outcome.clientCode === ERROR_CODE.CLIENT_DECRYPT) {
    reportClientError({ reportId: token, errorCode: ERROR_CODE.CLIENT_DECRYPT, clientRequestId: outcome.requestId })
  }
}

const state = {
  token: null,
  lang: null,
  base: {
    reportName: "",
    reportType: "",
    name: "",
    idNumber: "",
    age: "",
    medicalNumber: "",
    gender: 0,
    evaluateCount: 0,
    startTime: "",
    isFinish: null, // 缺鍵（V1／舊資料）＝null＝已完成（src/report/errors.js 的 reportReadiness；工單 17 站5）
    executionStatus: null,
    isSupportStatistics: false,
    downloadLink: ""
  },
  reportData: null, // record from getContestReportById
  reportSections: null, // 主報告回應頂層 sections（V2；後端工單 11），沒有就是 null——只有新渲染器讀
  statisticsData: null, // record from getContestReportStatisticsById
  reportRecords: null, // record from getReportRecordsById
  aiAnalysisData: null, // explanation and suggestions from getAIAnalysisById
  recommandData: {
    riskLevel: "",
    recommandTemplate: "",
    score: [],
    explanation: {}
  },
  // 動作歷史 /history（報告改版工單 25 PR-B；新渲染器的比較列用）：{ status, data, reason, category }，形狀與狀態意義見
  // src/report/history-state.js。刻意不放進 loading／error（isLoading／hasError 與舊渲染器的載入、錯誤顯示不受它影響）；
  // 結果另記 outcome.history（requestId）。
  history: initialHistoryState(),
  loading: {
    report: false,
    statistics: false,
    records: false,
    aiAnalysis: false,
    recommand: false
  },
  error: {
    report: null,
    statistics: null,
    records: null,
    aiAnalysis: null,
    recommand: null
  },
  outcome: emptyOutcome()
}

const mutations = {
  SET_TOKEN(state, token) {
    state.token = token
  },
  SET_LANG(state, lang) {
    state.lang = lang
  },
  SET_BASE(state, base) {
    state.base = { ...state.base, ...base }
  },
  SET_REPORT_DATA(state, data) {
    state.reportData = data
  },
  SET_REPORT_SECTIONS(state, sections) {
    state.reportSections = Array.isArray(sections) ? sections : null
  },
  SET_STATISTICS_DATA(state, data) {
    state.statisticsData = data
  },
  SET_REPORT_RECORDS(state, data) {
    state.reportRecords = data
  },
  SET_AI_ANALYSIS_DATA(state, data) {
    state.aiAnalysisData = data
  },
  SET_RECOMMAND_DATA(state, data) {
    state.recommandData = data
  },
  SET_LOADING(state, { type, status }) {
    state.loading[type] = status
  },
  SET_ERROR(state, { type, error }) {
    state.error[type] = error
  },
  SET_OUTCOME(state, { type, outcome }) {
    state.outcome[type] = outcome
  },
  SET_HISTORY(state, history) {
    state.history = history
  },
  CLEAR_DATA(state) {
    state.base = {
      reportName: "",
      reportType: "",
      name: "",
      idNumber: "",
      age: "",
      medicalNumber: "",
      gender: 0,
      evaluateCount: 0,
      startTime: "",
      isFinish: null,
      executionStatus: null,
      isSupportStatistics: false,
      downloadLink: ""
    }
    state.reportData = null
    state.reportSections = null
    state.statisticsData = null
    state.reportRecords = null
    state.aiAnalysisData = null
    state.recommandData = {
      riskLevel: "",
      recommandTemplate: "",
      score: [],
      explanation: {}
    }
    state.error = {
      report: null,
      statistics: null,
      records: null,
      aiAnalysis: null,
      recommand: null
    }
    state.history = initialHistoryState()
    state.outcome = emptyOutcome()
  }
}

// /discussion 本體（原 fetchRecommand 內容；請求範圍與報告世代見檔頭）。結束後記為本次頁殼載入內 settled。
async function fetchRecommandOnce({ commit, state }, { token, lang, force = false }) {
  // Check if recommand data is already populated (simple check on riskLevel or just assume if reportData is there, maybe recommand isn't)
  // Recommand data is separate, so check if we have it. 
  // Since default is empty structure, we can check if riskLevel is set or just a flag.
  // But let's just check if we have fetched it. 
  // To be safe, let's add a flag or just check if riskLevel is not empty string if that's guaranteed.
  // Or better, just rely on the fact that if we are here, we might need it. 
  // But to implement caching, we need to know if we have it.
  // Let's check if `state.recommandData.riskLevel` is set.
  // force（工單 17 站5）：主報告產生中期間取得的 /discussion 可能只有 level 沒有 AI，恢復時要真的重抓
  if (state.recommandData.riskLevel && !force) return

  const generation = currentGeneration
  commit('SET_LOADING', { type: 'recommand', status: true })

  let response = null
  let failure = null
  try {
    response = await getRecommandById(token, lang)
  } catch (error) {
    failure = error
  }
  // 換報告／換語言之後才回來的舊回應：丟掉（見檔頭「報告世代」）
  if (!tokenGenerations.isCurrent(generation)) return
  try {
    if (response) {
      const { data } = response
      const recommandData = {
        riskLevel: data.level,
        recommandTemplate: data.recommand,
        score: data.score,
        explanation: data.explanation || {}
      }
      commit('SET_RECOMMAND_DATA', recommandData)
      commit('SET_OUTCOME', { type: 'recommand', outcome: successOutcome(data, { requestId: response.requestId }) })
    }
  } catch (error) {
    failure = error
  }
  if (failure) {
    console.error(failure)
    // Recommand error might not block the whole page, but let's record it
    commit('SET_ERROR', { type: 'recommand', error: true })
    const outcome = outcomeOfError(failure)
    commit('SET_OUTCOME', { type: 'recommand', outcome })
    reportDecryptFailure(token, outcome)
  }
  commit('SET_LOADING', { type: 'recommand', status: false })
  requestScope.markSettled('recommand', token, lang)
}

// /history 本體（報告改版工單 25 PR-B）：次要請求——任何結果只記進 state.history（＋outcome.history），**不**丟錯、不分類成整頁畫面、
// 不排重試、不上報（只有解密失敗沿用上面 reportDecryptFailure 的既有規則：主／次要請求都上報、同報告只報一次）。
// 失敗只 console.warn（不是 console.error：端點尚未部署的 404 是預期的降級路徑）。結束後記為本次頁殼載入內 settled。
async function fetchHistoryOnce({ commit }, { token, lang }) {
  const generation = currentGeneration
  commit('SET_HISTORY', loadingHistoryState())

  let response = null
  let failure = null
  try {
    response = await getReportHistoryById(token, lang)
  } catch (error) {
    failure = error
  }
  // 換報告／換語言之後才回來的舊回應：丟掉（見檔頭「報告世代」）
  if (!tokenGenerations.isCurrent(generation)) return
  let next = null
  let outcome = null
  try {
    if (response) {
      next = historySuccessState(response.data)
      // 200 但形狀不對（沒有 series 陣列）：當舊後端非契約錯誤（回報碼類別段 HTTP200），同 apis/report.js 對主報告的處理
      outcome = next
        ? successOutcome(response.data, { requestId: response.requestId })
        : failureOutcome({ status: 200, requestId: response.requestId })
    } else {
      outcome = outcomeOfError(failure)
      reportDecryptFailure(token, outcome)
    }
  } catch (error) {
    outcome = failureOutcome({})
  }
  if (!next) next = historyFailureState(outcome)
  if (next.status === HISTORY_STATUS.FAILED) console.warn('[report] /history unavailable; comparison row degrades only', next.category)
  commit('SET_HISTORY', next)
  commit('SET_OUTCOME', { type: 'history', outcome })
  requestScope.markSettled('history', token, lang)
}

const actions = {
  // 報告頁殼每次 load()（掛載、換報告）開頭呼叫：作廢上一次頁殼載入的 settled 紀錄（見檔頭「請求範圍」）
  beginShellLoad() {
    requestScope.beginShellLoad()
  },

  initReport({ commit, state }, { token, lang }) {
    // If token or lang changed, clear data
    if (state.token !== token || state.lang !== lang) {
      commit('SET_TOKEN', token)
      commit('SET_LANG', lang)
      commit('CLEAR_DATA')
      requestScope.resetSettled()
      currentGeneration = tokenGenerations.next()
    }
  },

  // 只打 v1 報告端點：?version=v2 由前端忽略（工單 15；v2 端點由後端工單 16 移除）
  // force：強制重抓（舊渲染器「重新整理」鈕、頁殼錯誤畫面的自動與手動重試，工單 17）——不受「本次頁殼載入內已結束」
  // 限制，也不吃 reportData 快取（200 但未完成時 record 可能已有部分資料）；渲染器掛載、watcher 等自動流程一律不帶。
  // 一律帶 noWait=1（工單 17 第 7 點；spec Q38）：未完成不在後端 sleep，由頁殼自動重試。
  fetchReport({ commit, state, dispatch }, { token, lang, force = false }) {
    dispatch('initReport', { token, lang })

    // If data exists, don't fetch again
    if (state.reportData && !force) return Promise.resolve()
    // 本次頁殼載入內已結束過（失敗，或 200 但沒有 record）：自動流程不重打（見檔頭「請求範圍」）
    if (!force && requestScope.isSettled('report', token, lang)) return Promise.resolve()

    return requestScope.dedupe('report', token, lang, async () => {
      const generation = currentGeneration
      commit('SET_LOADING', { type: 'report', status: true })
      commit('SET_ERROR', { type: 'report', error: null })

      let response = null
      let failure = null
      try {
        response = await getContestReportById(token, lang, { noWait: true })
      } catch (error) {
        failure = error
      }
      // 換報告／換語言之後才回來的舊回應：丟掉（見檔頭「報告世代」）
      if (!tokenGenerations.isCurrent(generation)) return
      try {
        if (response) {
          const { data } = response
          commit('SET_BASE', data.base)
          commit('SET_REPORT_DATA', data.record)
          commit('SET_REPORT_SECTIONS', data.sections)
          commit('SET_OUTCOME', { type: 'report', outcome: successOutcome(data, { requestId: response.requestId }) })
        }
      } catch (error) {
        failure = error
      }
      if (failure) {
        console.error(failure)
        commit('SET_ERROR', { type: 'report', error: true })
        const outcome = outcomeOfError(failure)
        commit('SET_OUTCOME', { type: 'report', outcome })
        reportDecryptFailure(token, outcome)
      }
      commit('SET_LOADING', { type: 'report', status: false })
      requestScope.markSettled('report', token, lang)
    })
  },

  // force：強制重抓（工單 17：頁殼錯誤畫面重試成功、報告終於可以顯示時，補抓在不能顯示期間取得或失敗的 /discussion）
  fetchRecommand({ commit, state, dispatch }, { token, lang, force = false }) {
    dispatch('initReport', { token, lang })
    // 本次頁殼載入內同一 token＋lang 的 /discussion 已結束過（見檔頭「請求範圍」）：不重打；下次頁殼載入會重試
    if (!force && requestScope.isSettled('recommand', token, lang)) return Promise.resolve()
    return requestScope.dedupe('recommand', token, lang, () => fetchRecommandOnce({ commit, state }, { token, lang, force }))
  },

  // 動作歷史 /history（報告改版工單 25 PR-B）。發不發由呼叫端依比較列開關決定（src/report/compare-switch.js；目前＝報告頁殼
  // pages/Report/index.vue 與新渲染器換語言）——本 action 不看開關。非報告頁殼的頁面（例：工單 26 的趨勢頁）要用時，要先 dispatch
  // beginShellLoad 開自己的請求範圍，否則上一頁留下的 settled 會讓自動流程不打。規則同 /discussion：進行中共用 promise；本次頁殼
  // 載入內已結束（含失敗）的自動流程不重打；跨頁殼載入的快取短路＝結果已確定（historyIsFinal：ok，或 empty 且不是錨點尚未完成），
  // 失敗與「錨點尚未完成」下次頁殼載入才重試。force（頁殼錯誤畫面重試成功、主報告恢復時；結果不確定才帶）不受 settled 與快取限制；
  // 遇到在途請求（站5 D6）＝等它結束，結果仍不確定（且還是同一份報告／語言）才再打一次。回傳的 promise 永遠 resolve（失敗只記狀態）。
  fetchHistory({ commit, state, dispatch }, { token, lang, force = false }) {
    dispatch('initReport', { token, lang })
    if (!force && historyIsFinal(state.history)) return Promise.resolve()
    if (!force && requestScope.isSettled('history', token, lang)) return Promise.resolve()
    const run = () => fetchHistoryOnce({ commit }, { token, lang })
    if (!force) return requestScope.dedupe('history', token, lang, run)
    const generation = currentGeneration
    return requestScope.refreshAfterInflight('history', token, lang, run,
      () => tokenGenerations.isCurrent(generation) && !historyIsFinal(state.history))
  },

  async fetchStatistics({ commit, state, dispatch }, { token, lang }) {
    dispatch('initReport', { token, lang })

    if (state.statisticsData) return

    commit('SET_LOADING', { type: 'statistics', status: true })
    commit('SET_ERROR', { type: 'statistics', error: null })

    try {
      const response = await getContestReportStatisticsById(token, lang)
      const { data } = response
      commit('SET_BASE', data.base)
      commit('SET_STATISTICS_DATA', data.record)
      commit('SET_OUTCOME', { type: 'statistics', outcome: successOutcome(data, { requestId: response.requestId }) })
    } catch (error) {
      console.error(error)
      commit('SET_ERROR', { type: 'statistics', error: true })
      const outcome = outcomeOfError(error)
      commit('SET_OUTCOME', { type: 'statistics', outcome })
      reportDecryptFailure(token, outcome)
    } finally {
      commit('SET_LOADING', { type: 'statistics', status: false })
    }
  },

  async fetchReportRecords({ commit, state, dispatch }, { token, lang }) {
    dispatch('initReport', { token, lang })

    if (state.reportRecords) return

    commit('SET_LOADING', { type: 'records', status: true })
    commit('SET_ERROR', { type: 'records', error: null })

    try {
      const response = await getReportRecordsById(token, lang)
      const { data } = response
      commit('SET_REPORT_RECORDS', data.record)
      commit('SET_OUTCOME', { type: 'records', outcome: successOutcome(data, { requestId: response.requestId }) })
    } catch (error) {
      console.error(error)
      commit('SET_ERROR', { type: 'records', error: true })
      const outcome = outcomeOfError(error)
      commit('SET_OUTCOME', { type: 'records', outcome })
      reportDecryptFailure(token, outcome)
    } finally {
      commit('SET_LOADING', { type: 'records', status: false })
    }
  },

  async fetchAIAnalysis({ commit, state, dispatch }, { token, lang }) {
    dispatch('initReport', { token, lang })

    if (state.aiAnalysisData) return

    commit('SET_LOADING', { type: 'aiAnalysis', status: true })
    commit('SET_ERROR', { type: 'aiAnalysis', error: null })

    try {
      const response = await getAIAnalysisById(token, lang)
      const { data } = response
      commit('SET_BASE', data.base)
      // AI Analysis returns explanation and suggestions
      commit('SET_AI_ANALYSIS_DATA', {
        explanation: data.explanation,
        suggestions: data.suggestions
      })
      commit('SET_OUTCOME', { type: 'aiAnalysis', outcome: successOutcome(data, { requestId: response.requestId }) })
    } catch (error) {
      console.error(error)
      commit('SET_ERROR', { type: 'aiAnalysis', error: true })
      const outcome = outcomeOfError(error)
      commit('SET_OUTCOME', { type: 'aiAnalysis', outcome })
      reportDecryptFailure(token, outcome)
    } finally {
      commit('SET_LOADING', { type: 'aiAnalysis', status: false })
    }
  }
}

const getters = {
  isLoading: state => Object.values(state.loading).some(status => status),
  hasError: state => Object.values(state.error).some(error => error)
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
